import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Calendar, MapPin, Clock, User, Users, Briefcase, Edit, CheckCircle2, XCircle, Play, Flag, FileText, MessageSquare } from 'lucide-react';
import { fmtDateTime, fmtDate, fmtTime } from '@/lib/dates';
import { toast } from 'sonner';
import { fileUrl } from '@/components/Uploaders';

export default function AgendaDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agenda, setAgenda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, type: '' });
  const [reason, setReason] = useState('');
  const [kehadiranStatus, setKehadiranStatus] = useState('Hadir');

  const load = () => {
    setLoading(true);
    api.get(`/agenda/${id}`).then((r) => setAgenda(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const doAction = async (path, ok = 'Berhasil', err = 'Gagal') => {
    try {
      await api.post(`/agenda/${id}/${path}`, { catatan: reason });
      toast.success(ok);
      setModal({ open: false, type: '' });
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || err);
    }
  };

  if (loading || !agenda) return <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>;

  const isKabag = user?.role === 'admin' || user?.role === 'kabag';
  const canEdit = user?.role === 'admin' || user?.role === 'operator';
  const canStart = (isKabag || user?.role === 'operator') && agenda.status === 'Approved';
  const canComplete = (isKabag || user?.role === 'operator') && (agenda.status === 'Sedang Berlangsung' || agenda.status === 'Approved');
  const canConfirm = agenda.delegasi_id === user?.id && agenda.status_kehadiran !== 'Konfirmasi Hadir' && !['Hadir', 'Tidak Hadir', 'Berhalangan'].includes(agenda.status_kehadiran);

  const confirmAttendance = async () => {
    try {
      await api.post(`/agenda/${id}/confirm-attendance`, { catatan: `${kehadiranStatus}|${reason}` });
      toast.success('Kehadiran dikonfirmasi');
      setModal({ open: false, type: '' });
      setReason('');
      load();
    } catch (e) {
      toast.error('Gagal');
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        title={agenda.nama_acara}
        subtitle={agenda.deskripsi || 'Detail agenda pimpinan'}
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Agenda', to: '/agenda' }, { label: 'Detail' }]}
        action={
          <>
            <Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Kembali</Button>
            {canEdit && agenda.status === 'Pending' && (
              <Link to={`/agenda/edit/${id}`}><Button variant="outline"><Edit className="w-4 h-4 mr-2" />Edit</Button></Link>
            )}
          </>
        }
      />

      {/* Status banner */}
      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-5 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <StatusBadge status={agenda.status} type="agenda" />
            <StatusBadge status={agenda.tingkat_urgensi} type="urgensi" />
            <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold">{agenda.kategori}</span>
          </div>
          <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
            {isKabag && agenda.status === 'Pending' && (
              <>
                <Button data-testid="approval-approve-button" onClick={() => setModal({ open: true, type: 'approve' })} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-2" />Setujui</Button>
                <Button data-testid="approval-reject-button" onClick={() => setModal({ open: true, type: 'reject' })} variant="destructive"><XCircle className="w-4 h-4 mr-2" />Tolak</Button>
              </>
            )}
            {canStart && <Button onClick={() => doAction('start', 'Agenda dimulai')} variant="outline"><Play className="w-4 h-4 mr-2" />Mulai Kegiatan</Button>}
            {canComplete && agenda.status !== 'Selesai' && <Button onClick={() => doAction('complete', 'Agenda diselesaikan')} variant="outline"><Flag className="w-4 h-4 mr-2" />Selesaikan</Button>}
            {canConfirm && <Button data-testid="confirm-attendance-button" onClick={() => setModal({ open: true, type: 'kehadiran' })} className="bg-[#DC2626] hover:bg-[#B91C1C]"><CheckCircle2 className="w-4 h-4 mr-2" />Konfirmasi Kehadiran</Button>}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold">Detail Acara</CardTitle></CardHeader>
            <CardContent className="divide-y divide-slate-100">
              <InfoRow icon={Calendar} label="Tanggal Mulai" value={fmtDateTime(agenda.tanggal_mulai)} />
              <InfoRow icon={Clock} label="Tanggal Selesai" value={fmtDateTime(agenda.tanggal_selesai)} />
              <InfoRow icon={MapPin} label="Lokasi" value={agenda.lokasi} />
              <InfoRow icon={Briefcase} label="Pakaian" value={agenda.pakaian || '-'} />
              <InfoRow icon={User} label="PIC / Kontak" value={agenda.pic_kontak || '-'} />
              {agenda.deskripsi && (
                <div className="py-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Deskripsi</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{agenda.deskripsi}</p>
                </div>
              )}
              {agenda.catatan && (
                <div className="py-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Catatan</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{agenda.catatan}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold">Pejabat & Delegasi</CardTitle></CardHeader>
            <CardContent className="space-y-2.5">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pejabat Utama</div>
                <div className="font-semibold text-slate-800 mt-0.5">{agenda.pejabat_utama_nama || agenda.pejabat_utama}</div>
              </div>
              {agenda.delegasi_nama && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Didelegasikan Kepada</div>
                  <div className="font-semibold text-slate-800 mt-0.5">{agenda.delegasi_nama}</div>
                  <div className="text-xs text-slate-500">Status kehadiran: <span className="font-semibold">{agenda.status_kehadiran || 'Belum'}</span></div>
                  {agenda.disposisi_catatan && (
                    <div className="mt-2 pt-2 border-t border-amber-200">
                      <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Disposisi Pimpinan</div>
                      <p className="text-xs text-slate-700 mt-1 italic">"{agenda.disposisi_catatan}"</p>
                    </div>
                  )}
                  {agenda.konfirmasi_catatan && (
                    <div className="mt-2 pt-2 border-t border-amber-200">
                      <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Catatan Delegasi</div>
                      <p className="text-xs text-slate-700 mt-1">{agenda.konfirmasi_catatan}</p>
                    </div>
                  )}
                </div>
              )}
              {agenda.pendamping_nama?.length > 0 && (
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pendamping</div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {agenda.pendamping_nama.map((p, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-white border border-slate-200 text-xs font-medium text-slate-700">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {agenda.dokumen?.length > 0 && (
            <Card className="border-slate-200 shadow-premium">
              <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><FileText className="w-4 h-4 text-[#DC2626]" />Dokumen Pendukung</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {agenda.dokumen.map((d, i) => (
                    <a key={i} href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 rounded border border-slate-200 hover:border-red-300 hover:bg-red-50/30">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="flex-1 text-sm font-medium text-slate-700">{d.name}</span>
                      {d.kind && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{d.kind}</span>}
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Side: audit-like info */}
        <div className="space-y-6">
          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold">Riwayat Persetujuan</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-3">
              <Timeline icon={Calendar} title="Agenda dibuat" subtitle={`Oleh ${agenda.created_by_nama || 'Operator'}`} time={fmtDateTime(agenda.created_at)} />
              {agenda.approved_at && <Timeline icon={CheckCircle2} title="Disetujui Kabag" subtitle="Status: Approved" time={fmtDateTime(agenda.approved_at)} color="emerald" />}
              {agenda.rejected_at && <Timeline icon={XCircle} title="Ditolak" subtitle={agenda.rejection_reason || '-'} time={fmtDateTime(agenda.rejected_at)} color="red" />}
              {agenda.status === 'Selesai' && <Timeline icon={Flag} title="Selesai" subtitle="Kegiatan telah dilaksanakan" time={fmtDateTime(agenda.updated_at)} color="emerald" />}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={modal.open} onOpenChange={(o) => setModal({ ...modal, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {modal.type === 'approve' ? 'Setujui Agenda' : modal.type === 'reject' ? 'Tolak Agenda' : 'Konfirmasi Kehadiran'}
            </DialogTitle>
          </DialogHeader>
          {modal.type === 'kehadiran' ? (
            <>
              <p className="text-sm text-slate-600">Anda didelegasikan untuk agenda ini. Mohon konfirmasi kehadiran Anda.</p>
              <div className="grid grid-cols-3 gap-2">
                {['Hadir', 'Tidak Hadir', 'Berhalangan'].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setKehadiranStatus(s)}
                    data-testid={`kehadiran-${s.toLowerCase().replace(/ /g, '-')}`}
                    className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                      kehadiranStatus === s
                        ? s === 'Hadir' ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : s === 'Tidak Hadir' ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Textarea placeholder="Catatan / alasan (opsional)" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                {modal.type === 'approve'
                  ? 'Anda akan menyetujui agenda ini. Operator akan menerima notifikasi.'
                  : 'Berikan alasan penolakan agar operator dapat melakukan revisi.'}
              </p>
              <Textarea
                data-testid="approval-reason"
                placeholder={modal.type === 'approve' ? 'Catatan (opsional)' : 'Alasan penolakan...'}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false, type: '' })}>Batal</Button>
            {modal.type === 'approve' && (
              <Button data-testid="confirm-approve" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => doAction('approve', 'Agenda berhasil disetujui')}>Setujui</Button>
            )}
            {modal.type === 'reject' && (
              <Button data-testid="confirm-reject" variant="destructive" onClick={() => doAction('reject', 'Agenda ditolak')}>Tolak Agenda</Button>
            )}
            {modal.type === 'kehadiran' && (
              <Button data-testid="submit-kehadiran" className="bg-[#DC2626] hover:bg-[#B91C1C]" onClick={confirmAttendance}>Kirim Konfirmasi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="py-3 flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-sm text-slate-800 font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function Timeline({ icon: Icon, title, subtitle, time, color = 'slate' }) {
  const colors = {
    slate: 'bg-slate-100 text-slate-600',
    emerald: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
  };
  return (
    <div className="flex gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="font-semibold text-slate-800 text-sm">{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{time}</div>
      </div>
    </div>
  );
}
